import React, { FC, useMemo, useState } from 'react';
import { AllieCard } from '../../../components';
import { AllieProductionOrderStatusChip } from
  '../ProductionOrderStatusChip/ProductionOrderStatusChip';
import {
  ActualBar,
  CardOuter,
  CompletionBadge,
  ExpandedSection,
  ExpandToggleRow,
  HourLabel,
  HourLabelsRow,
  LineTitle,
  MetaBadge,
  MetaRow,
  MiniBarCol,
  MiniChart,
  MiniChartWrap,
  NoDataText,
  PlantLabel,
  ProducedValue,
  SummaryBody,
  TargetMark,
  TargetMinMark,
  TargetText,
  TitleBlock,
  ValuesRow,
} from './LineMonitoringCard.styles';
import type { LineMonitoringCardProps } from './LineMonitoringCard.types';

const computeLevel = (pct: number): 'success' | 'warning' | 'error' => {
  if (pct >= 80) return 'success';
  if (pct >= 40) return 'warning';
  return 'error';
};

const ChevronDown: FC<{ size?: number }> = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24"
    fill="none" aria-hidden="true">
    <path d="M6 9l6 6 6-6" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ChevronUp: FC<{ size?: number }> = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24"
    fill="none" aria-hidden="true">
    <path d="M18 15l-6-6-6 6" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/**
 * Station / line card for mobile monitoring lists (native app).
 * Presentation-only; callers supply normalized bar data and labels.
 *
 * When `collapsible={true}`:
 *  - Default: collapsed — shows meta, title, values row, chart + "Ver más" toggle.
 *  - Expanded: additionally renders `expandedContent` (OP, product, KPIs, CTA) + "Ver menos".
 *  - The toggle STOPS propagation so the card-level `onClick` (navigation) is NOT triggered.
 *  - The clickable region for navigation covers ONLY the always-visible summary section,
 *    NOT the expanded area, so the "Ver detalles completos" button can call navigate independently.
 */
export const LineMonitoringCard: FC<LineMonitoringCardProps> = ({
  title,
  plantLabel,
  metaBadge,
  statusChip,
  producedPrimary,
  targetSecondary,
  completionPercent,
  completionLevel: completionLevelProp,
  hourBars,
  hourLabels,
  showTargets = true,
  collapsible = false,
  expandedContent,
  expandLabel = 'Ver más',
  collapseLabel = 'Ver menos',
  onClick,
  className,
  noDataLabel,
}) => {
  const [expanded, setExpanded] = useState(false);

  const level = completionLevelProp ?? computeLevel(completionPercent);

  const safeLabels = useMemo(() => {
    const n = Math.min(hourBars.length, hourLabels.length);
    return hourLabels.slice(0, n);
  }, [hourBars.length, hourLabels]);

  const handleToggle = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation();
    setExpanded((prev) => !prev);
  };

  /* Summary section — always visible and acts as the tap-to-navigate target.
     Uses a native <button> when clickable to satisfy accessibility requirements. */
  const effectiveOnClick = onClick;
  const SummaryTag = effectiveOnClick ? 'button' : 'div';

  const summary = (
    <SummaryTag
      {...(effectiveOnClick
        ? {
          type: 'button' as const,
          onClick: effectiveOnClick,
          'aria-label': title,
        }
        : {})}
      style={{
        all: 'unset' as React.CSSProperties['all'],
        display: 'block',
        width: '100%',
        cursor: effectiveOnClick ? 'pointer' : 'default',
        boxSizing: 'border-box' as React.CSSProperties['boxSizing'],
      }}
    >
      <SummaryBody>
        {/* ── Meta row: date/shift badge + status chip ── */}
        <MetaRow>
          {metaBadge ? <MetaBadge title={metaBadge}>{metaBadge}</MetaBadge> : <span />}
          <AllieProductionOrderStatusChip
            status={statusChip.status}
            statusLabel={statusChip.statusLabel}
          />
        </MetaRow>

        {/* ── Plant + line name ── */}
        <TitleBlock>
          {plantLabel ? <PlantLabel>{plantLabel}</PlantLabel> : null}
          <LineTitle>{title}</LineTitle>
        </TitleBlock>

        {noDataLabel ? (
          <NoDataText>{noDataLabel}</NoDataText>
        ) : (
          <>
            {/* ── Produced / target / completion % ── */}
            <ValuesRow>
              <ProducedValue>{producedPrimary}</ProducedValue>
              <TargetText>{targetSecondary}</TargetText>
              <CompletionBadge $level={level}>{completionPercent}%</CompletionBadge>
            </ValuesRow>

            {/* ── Hourly mini bar chart ── */}
            {hourBars.length > 0 && (
              <MiniChartWrap>
                <MiniChart>
                  {hourBars.map((h, i) => (
                    <MiniBarCol key={`${hourLabels[i] ?? 'h'}-${i}`}>
                      {showTargets && h.targetPct !== undefined && (
                        <TargetMark $heightPct={h.targetPct} />
                      )}
                      {showTargets && h.targetMinPct !== undefined && (
                        <TargetMinMark $heightPct={h.targetMinPct} />
                      )}
                      <ActualBar $heightPct={h.producedPct} $status={h.barStatus ?? 'none'} />
                    </MiniBarCol>
                  ))}
                </MiniChart>
                <HourLabelsRow>
                  {safeLabels.map((lab, i) => (
                    <HourLabel key={`${lab}-${i}`}>{lab}</HourLabel>
                  ))}
                </HourLabelsRow>
              </MiniChartWrap>
            )}
          </>
        )}
      </SummaryBody>
    </SummaryTag>
  );

  return (
    <CardOuter className={className}>
      <AllieCard
        style={{
          borderRadius: '8px',
          padding: '16px',
          boxSizing: 'border-box',
          boxShadow: 'none',
          border: '1px solid var(--allie-color-neutral-20)',
        }}
        content={{
          style: { padding: 0 },
          children: (
            <>
              {summary}

              {/* ── Expanded content: rendered outside the clickable summary ── */}
              {!noDataLabel && collapsible && expandedContent && (
                <ExpandedSection $open={expanded}>
                  {expandedContent}
                </ExpandedSection>
              )}

              {/* ── Non-collapsible mode: always show expandedContent ── */}
              {!noDataLabel && !collapsible && expandedContent}

              {/* ── Ver más / Ver menos toggle ── */}
              {!noDataLabel && collapsible && expandedContent && (
                <ExpandToggleRow
                  type="button"
                  onClick={handleToggle}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') handleToggle(e);
                  }}
                  aria-expanded={expanded}
                >
                  {expanded ? collapseLabel : expandLabel}
                  {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                </ExpandToggleRow>
              )}
            </>
          ),
        }}
      />
    </CardOuter>
  );
};

LineMonitoringCard.displayName = 'LineMonitoringCard';
